import { apiGet, apiGetOptional, apiPost } from '@/api/http';

import type { Message } from '@/types/interview';

const api_url = import.meta.env.VITE_API_URL;

export type UnreadMessagesSummary = {
  unread_message_cnt: number;
  client_companies?: Array<{
    id: string;
    unread_message_cnt: number;
    interviews: Array<{ id: string; unread_message_cnt: number }>;
  }>;
  recruiters?: Array<{
    id: string;
    unread_message_cnt: number;
    interviews: Array<{ id: string; unread_message_cnt: number }>;
  }>;
};

export const getSupportMessageCounter = () =>
  apiGet<{ unread_message_cnt: number }>(
    `${api_url}conversations/users/admin/unread-messages/count/`,
  );

export const getUnreadMessagesCount = () =>
  apiGet<UnreadMessagesSummary>(
    `${api_url}conversations/unread-messages/count/`,
  );

export const markAllMessagesAsRead = () =>
  apiPost(`${api_url}conversations/mark-all-as-read/`);

export const getAdminContacts = (query = '') =>
  apiGet(`${api_url}conversations/users/${query}`);

export const getAllConversations = (query = '') =>
  apiGet(`${api_url}conversations/all/${query}`);

export const getUserRoom = (userId: string, query = '') =>
  apiGetOptional(`${api_url}conversations/users/${userId}/messages/${query}`);

export const getSupportRoom = (query = '') =>
  apiGet(`${api_url}conversations/users/admin/messages/${query}`);

export const getInterviewRoom = (interviewId: string, query = '') =>
  apiGetOptional(
    `${api_url}conversations/interviews/${interviewId}/messages/${query}`,
  );

export const getDirectConversation = (
  recruiterUserOrClientCompanyId: string,
  query = '',
) =>
  apiGetOptional(
    `${api_url}conversations/direct/${recruiterUserOrClientCompanyId}/messages/${query}`,
  );

export const sendUserMessage = (
  userId: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${api_url}conversations/users/${userId}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(`${api_url}conversations/users/${userId}/messages/`, {
    text,
  });
};

export const sendSupportMessage = (text: string, file?: File | Blob) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${api_url}conversations/users/admin/messages/`,
      formData,
    );
  }
  return apiPost<Message>(`${api_url}conversations/users/admin/messages/`, {
    text,
  });
};

export const sendInterviewMessage = (
  interviewId: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${api_url}conversations/interviews/${interviewId}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${api_url}conversations/interviews/${interviewId}/messages/`,
    { text },
  );
};

export const sendDirectMessage = (
  otherParty: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${api_url}conversations/direct/${otherParty}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${api_url}conversations/direct/${otherParty}/messages/`,
    { text },
  );
};

export const submitStageScreeningAnswers = (
  interviewId: string,
  answers: Record<string, unknown>,
  stage: string,
) =>
  apiPost(
    `${api_url}interviews/${interviewId}/stage-screening-questions/answer/`,
    { answers, stage },
  );
